/**
 * Плашка доступа + церемония запроса.
 *
 *   - 'not_requested' → кнопка «Запросить доступ». При нажатии
 *                       5-секундная кинематографичная анимация
 *                       со звуком (ElevenLabs sfx), после чего
 *                       UI переходит в состояние 'pending'.
 *   - 'pending'       → «Запрос отправлен, ожидайте».
 *   - 'blocked'       → «Доступ отклонён».
 *
 * Рендерится поверх приложения, когда backend отвечает 403 на
 * /auth/telegram с соответствующим кодом.
 */
import { useEffect, useRef, useState, type FC } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { AccessState } from '@/contexts/AuthContext';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';

interface Props {
  state: AccessState;
  onClose?: () => void;
  /** Клик по «Запросить доступ» (только для state='not_requested') */
  onRequestAccess?: () => void;
  /** Идёт ли сейчас запрос — блокирует повторный клик */
  requesting?: boolean;
}

// Длительность церемонии ровно 5 сек — синхронно с длиной sfx.
const CEREMONY_MS = 5000;

const CEREMONY_STEPS: Array<{ t: number; key: string; icon: string }> = [
  { t: 0,    key: 'accessGate.step1', icon: '🔑' },
  { t: 1200, key: 'accessGate.step2', icon: '🔐' },
  { t: 2400, key: 'accessGate.step3', icon: '📡' },
  { t: 3600, key: 'accessGate.step4', icon: '📝' },
  { t: 4600, key: 'accessGate.step5', icon: '✅' },
];

export const AccessGate: FC<Props> = ({
  state,
  onClose,
  onRequestAccess,
  requesting,
}) => {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const sounds = useUISounds();
  const [ceremony, setCeremony] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const timersRef = useRef<number[]>([]);

  const startCeremony = () => {
    if (ceremony || requesting) return;
    // Тяжёлый haptic + объёмное «нажатие»
    haptic.heavy();
    sounds.accessRequest();
    setCeremony(true);
    setStepIdx(0);

    // Шаги текста — пошаговая смена
    const lastIdx = CEREMONY_STEPS.length - 1;
    for (let i = 1; i < CEREMONY_STEPS.length; i++) {
      const id = window.setTimeout(() => {
        setStepIdx(i);
        if (i === lastIdx) {
          // Финальный шаг «Заявка отправлена ✓» — играем звук
          // одобрения (тот же, что подтверждает активацию слота)
          sounds.slotConfirmed();
          haptic.success();
        } else {
          // Лёгкий haptic на промежуточных — ощущение «живого»
          haptic.light();
        }
      }, CEREMONY_STEPS[i]!.t);
      timersRef.current.push(id);
    }

    // Через 5 сек — реально вызываем requestAccess, после чего бэк
    // переводит статус в 'pending' и AuthContext отрисует экран ожидания.
    const finishId = window.setTimeout(() => {
      haptic.success();
      onRequestAccess?.();
      // Анимация завершена — но не сбрасываем ceremony=false,
      // так как AuthContext сразу перерисует экран с state='pending'.
    }, CEREMONY_MS);
    timersRef.current.push(finishId);
  };

  useEffect(() => {
    return () => {
      for (const t of timersRef.current) window.clearTimeout(t);
    };
  }, []);

  let icon: string;
  let title: string;
  let body: string;
  if (state === 'not_requested') {
    icon = '🔐';
    title = t('accessGate.notRequestedTitle');
    body = t('accessGate.notRequestedBody');
  } else if (state === 'pending') {
    icon = '⏳';
    title = t('accessGate.pendingTitle');
    body = t('accessGate.pendingBody');
  } else if (state === 'outdated_link') {
    icon = '🔗';
    title = 'Устаревшая ссылка';
    body = 'Это устаревшая ссылка.\n\nВозьмите свежую у Вашего аплайнера.';
  } else {
    icon = '🚫';
    title = t('accessGate.blockedTitle');
    body = t('accessGate.blockedBody');
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background:
          'radial-gradient(ellipse at top, #1a1530 0%, #0d0b1f 60%, #050410 100%)',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          maxWidth: '360px',
          width: '100%',
          background: 'rgba(255, 255, 255, 0.04)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '24px',
          padding: '36px 28px',
          textAlign: 'center',
          boxShadow: '0 24px 60px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Иконка: обычный emoji ИЛИ кольцо прогресса во время церемонии */}
        {ceremony && state === 'not_requested' ? (
          <CeremonyRing />
        ) : (
          <motion.div
            key={state}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 280, damping: 18 }}
            style={{ fontSize: '56px', marginBottom: '16px' }}
          >
            {icon}
          </motion.div>
        )}

        {/* Заголовок / текст — в церемонии заменяются на шаги */}
        <AnimatePresence mode="wait">
          {ceremony && state === 'not_requested' ? (
            <motion.div
              key={`step-${stepIdx}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              style={{
                fontSize: '14px',
                fontFamily: 'ui-monospace, monospace',
                letterSpacing: '0.02em',
                color: '#E9E4FF',
                marginBottom: '28px',
                minHeight: '54px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontSize: '20px' }}>
                {CEREMONY_STEPS[stepIdx]!.icon}
              </span>
              <span>{t(CEREMONY_STEPS[stepIdx]!.key)}</span>
            </motion.div>
          ) : (
            <motion.div
              key={`static-${state}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: '#F5F3FF',
                  marginBottom: '14px',
                }}
              >
                {title}
              </div>
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: 1.55,
                  color: 'rgba(245, 243, 255, 0.68)',
                  whiteSpace: 'pre-line',
                  marginBottom: '28px',
                }}
              >
                {body}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Кнопка */}
        {state === 'not_requested' && !ceremony ? (
          <BigAccessButton
            onPress={startCeremony}
            disabled={requesting}
            label={t('accessGate.requestBtn')}
          />
        ) : state === 'not_requested' && ceremony ? (
          <div
            style={{
              height: '52px',
              borderRadius: '14px',
              background: 'rgba(124, 92, 255, 0.15)',
              border: '1px solid rgba(124, 92, 255, 0.3)',
              color: '#C9B8FF',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            {t('accessGate.processing')}
          </div>
        ) : onClose ? (
          <button
            onClick={onClose}
            style={{
              appearance: 'none',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              background: 'rgba(255, 255, 255, 0.04)',
              color: '#F5F3FF',
              padding: '12px 24px',
              borderRadius: '14px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {t('accessGate.close')}
          </button>
        ) : null}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// BigAccessButton — объёмная кнопка с «тяжёлым» press-эффектом.
// Цель: юзер чувствует отклик физически. Scale 0.92 + longer spring
// decay + inner shadow + glow — всё вместе даёт «плотность».
// ────────────────────────────────────────────────────────────────────
function BigAccessButton({
  onPress, disabled, label,
}: {
  onPress: () => void;
  disabled?: boolean;
  label: string;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <motion.button
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onClick={onPress}
      disabled={disabled}
      animate={{
        scale: pressed ? 0.92 : 1,
        boxShadow: pressed
          ? '0 2px 8px -2px rgba(124, 92, 255, 0.4), inset 0 3px 8px rgba(0,0,0,0.35)'
          : '0 16px 32px -10px rgba(124, 92, 255, 0.7), inset 0 1px 0 rgba(255,255,255,0.2)',
      }}
      transition={{
        // Медленный spring — тяжёлое ощущение при отпускании
        type: 'spring',
        stiffness: 240,
        damping: 22,
        mass: 1.2,
      }}
      style={{
        appearance: 'none',
        border: 'none',
        background: disabled
          ? 'rgba(124, 92, 255, 0.4)'
          : 'linear-gradient(180deg, #9B7FFF 0%, #6A4BFF 100%)',
        color: '#F5F3FF',
        padding: '16px 24px',
        borderRadius: '16px',
        fontSize: '15px',
        fontWeight: 700,
        letterSpacing: '0.02em',
        cursor: disabled ? 'default' : 'pointer',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Мягкое свечение в центре (не движется) */}
      <span
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, rgba(255,255,255,0.18) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <span style={{ position: 'relative', zIndex: 1 }}>{label}</span>
    </motion.button>
  );
}

// ────────────────────────────────────────────────────────────────────
// CeremonyRing — кольцо прогресса через чистый CSS @keyframes.
// Никакого JS-requestAnimationFrame, никаких React setState — браузер
// обрабатывает анимацию нативно. Работает плавно даже на слабых
// Android'ах и в in-app WebView.
// ────────────────────────────────────────────────────────────────────
const SIZE = 120;
const STROKE = 4;
const R = (SIZE - STROKE) / 2;
const C = 2 * Math.PI * R;

function CeremonyRing() {
  return (
    <div
      style={{
        width: SIZE,
        height: SIZE,
        margin: '0 auto 18px',
        position: 'relative',
      }}
    >
      {/* CSS keyframes живут внутри компонента — только когда церемония
          активна. Скоуп глобальный, но имя уникальное. */}
      <style>{`
        @keyframes lunaAccessRingFill {
          from { stroke-dashoffset: ${C}; }
          to   { stroke-dashoffset: 0; }
        }
        .luna-access-ring-progress {
          animation: lunaAccessRingFill ${CEREMONY_MS}ms linear forwards;
          /* will-change — подсказка GPU сделать отдельный слой */
          will-change: stroke-dashoffset;
        }
        @keyframes lunaAccessKeyPulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
        .luna-access-key {
          animation: lunaAccessKeyPulse 1.2s ease-in-out infinite;
          will-change: transform;
        }
      `}</style>
      <svg
        width={SIZE}
        height={SIZE}
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={STROKE}
          fill="none"
        />
        <circle
          className="luna-access-ring-progress"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          stroke="url(#luna-progress-grad)"
          strokeWidth={STROKE}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={C}
          style={{
            filter: 'drop-shadow(0 0 6px rgba(155, 127, 255, 0.7))',
          }}
        />
        <defs>
          <linearGradient id="luna-progress-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9B7FFF" />
            <stop offset="100%" stopColor="#6A4BFF" />
          </linearGradient>
        </defs>
      </svg>
      {/* Emoji-ключ в центре с пульсом через CSS (GPU-accelerated) */}
      <div
        className="luna-access-key"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '46px',
        }}
      >
        🔑
      </div>
    </div>
  );
}
